import DialpadIcon from "@mui/icons-material/Dialpad";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import FormInput from "../../Inputs/FormInput";
import useFetch from "../../../../hooks/useFetch";
import ChangePinService from "../../../../services/changePin.service";
import { notificationSliceActions } from "../../../../store/slices/notification";
import { useAppDispatch } from "../../../../store/store";
import ModalComponent from "../../Modal";
import { changePinSchema, TChangePinForm } from "./changePinSchema";
import { IChangePinModal } from "./models";

const translationPath = "page.changePin.";

const changePinService = new ChangePinService();

const pinInputProps = {
  inputMode: "numeric" as const,
  maxLength: 4,
  autoComplete: "one-time-code",
};

export const ChangePinModal = memo(({ open, onClose }: IChangePinModal) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { sendRequest, loading } = useFetch(changePinService.create);

  const methods = useForm<TChangePinForm>({
    resolver: zodResolver(changePinSchema),
    defaultValues: { newPin: "", confirmNewPin: "" },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const onSubmitHandler = useCallback(
    async (values: TChangePinForm) => {
      const { success } = await sendRequest({ newPin: values.newPin });
      if (success) {
        dispatch(
          notificationSliceActions.showNotification({
            message: `${t(translationPath + "success_message")}`,
            type: "success",
          })
        );
        onClose();
      }
    },
    [sendRequest, dispatch, t, onClose]
  );

  return (
    <ModalComponent open={open} onClose={onClose} dialogMaxWidth={"xs"}>
      <FormProvider {...methods}>
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitHandler)}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minWidth: { xs: "auto", sm: 340 },
          }}
        >
          <Avatar
            sx={(theme) => ({
              bgcolor:
                theme.palette.mode === "light"
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
              width: 56,
              height: 56,
              mb: 1.5,
            })}
          >
            <DialpadIcon />
          </Avatar>
          <Typography variant="h6" textAlign="center">
            {t(translationPath + "change_pin")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mb={3}
          >
            {t(translationPath + "subtitle")}
          </Typography>

          <Stack spacing={2} width="100%">
            <FormInput
              size="small"
              name="newPin"
              label={t(translationPath + "new_pin")}
              fullWidth
              autoFocus
              type="password"
              inputProps={pinInputProps}
            />
            <FormInput
              size="small"
              name="confirmNewPin"
              label={t(translationPath + "confirm_new_pin")}
              fullWidth
              type="password"
              inputProps={pinInputProps}
            />
          </Stack>

          <Box sx={{ display: "flex", gap: 1, width: "100%", mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              disabled={loading}
              onClick={onClose}
            >
              {t(translationPath + "cancel")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              size="small"
              type="submit"
              loading={loading}
            >
              {t(translationPath + "save")}
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </ModalComponent>
  );
});

export default ChangePinModal;
